import { useEffect, useState } from 'react';
import { Copy, Trash2 } from 'lucide-react';
import { partnerApi, type PartnerInvite } from '../../shared/api/partner.api';
import { ROUTES } from '../../shared/config/routes';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { confirmDialog } from '../../shared/ui/ConfirmHost';
import { toast } from 'sonner@2.0.3';

const linkOf = (token: string) => `${window.location.origin}${ROUTES.PARTNER_JOIN}?token=${encodeURIComponent(token)}`;
const day = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

/**
 * Партнёры (23.10.2026): внешние люди подключают свои аккаунты, мы видим
 * статистику их видео в «Статистике» под меткой «Партнёр». Здесь — ссылки-
 * приглашения: создать, скопировать, отозвать, и кто по ним пришёл.
 */
export function PartnersSection() {
  const [invites, setInvites] = useState<PartnerInvite[] | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => partnerApi.invites().then((r) => setInvites(r.invites)).catch((e) => toast.error(e.message));
  useEffect(() => { void load(); }, []);

  const copy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(linkOf(token));
      toast.success('Ссылка скопирована');
    } catch {
      toast.message(linkOf(token));
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { invite } = await partnerApi.createInvite(name);
      setName('');
      await load();
      await copy(invite.token);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (invite: PartnerInvite) => {
    if (!(await confirmDialog({ title: `Отозвать ссылку для «${invite.name}»?`, description: 'По ней больше нельзя будет войти.', confirmText: 'Отозвать', destructive: true }))) return;
    try {
      await partnerApi.revokeInvite(invite.token);
      await load();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <section className="space-y-3">
      <div>
        <p className="text-sm text-muted-foreground">
          Внешние люди видят только страницу подключения своих Instagram, TikTok, YouTube и Threads. Их аккаунты попадают в «Статистику» с меткой «Партнёр».
        </p>
      </div>
      <form onSubmit={create} className="flex flex-wrap gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Для кого ссылка — имя или компания" className="max-w-xs" required minLength={2} />
        <Button type="submit" size="sm" disabled={busy}>{busy ? 'Создаём…' : 'Создать ссылку'}</Button>
      </form>
      {invites && invites.length > 0 && (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-3 text-left">Для кого</th>
                <th className="p-3 text-left">Вход</th>
                <th className="p-3 text-left">Аккаунты</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.token} className="border-t">
                  <td className="p-3">
                    {invite.name}
                    <div className="text-xs text-muted-foreground">ссылка от {day(invite.createdAt)}</div>
                  </td>
                  <td className="p-3">
                    {invite.partner
                      ? <>{invite.partner.login}<div className="text-xs text-muted-foreground">вошёл {invite.usedAt ? day(invite.usedAt) : ''}</div></>
                      : <span className="text-muted-foreground">ещё не входил</span>}
                  </td>
                  <td className="p-3">
                    {invite.partner?.accounts.length
                      ? invite.partner.accounts.map((u) => <div key={u}>@{u}</div>)
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-3 text-right">
                    {!invite.partner && (
                      <span className="inline-flex gap-1">
                        <Button type="button" size="sm" variant="outline" onClick={() => void copy(invite.token)}><Copy />Ссылка</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => void revoke(invite)} aria-label="Отозвать ссылку"><Trash2 /></Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
